import React, { Component } from 'react';
import Stats from 'stats.js';

import { scaleTime, scaleLinear, scaleOrdinal } from 'd3-scale';
import debounce from 'lodash/debounce';
import { nest } from 'd3-collection';
import { line } from 'd3-shape';
import { mean } from 'd3-array';

import { polynomial } from 'regression';

import Data from './Data.js';

import Divisions, {data as divisionData} from './Divisions.js';
import PixiCanvas from './PixiCanvas';

import facebookLogo from './facebook.png';
import twitterLogo from './twitter.png';

import './App.css';

const framesPerDatum = 80
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const wetColor = '#619ba0';
const dryColor = '#d72832';

const wetScale = scaleLinear()
  .domain([0, 8])
  .range(['#fff', wetColor])
  .clamp(true)
const dryScale = scaleLinear()
  .domain([0, -8])
  .range(['#fff', dryColor])
  .clamp(true)

const sparklineWidth = 100;
const sparklineHeight = 20;

class App extends Component {
  constructor(props) {
    super(props);

    const compact = 815;
    const tiny = 580;
    this.metrics = {
      compact: compact,
      tiny: tiny,
      padding: 24,
      axisPadding: 48,
      fontSize: 16 * 0.75,
      minHeight: 768,
      divisionSize: 144,
      deviation: 4,
    };

    this.scales = {
      x: scaleTime(),
      y: scaleLinear(),
      c: scaleOrdinal(),
    };

    this.state = {
      lines: [],
      data: [],
      divisions: {},
      selectedDate: null,
      ...this._getDimensions(),
    };

    this.updateData = this.updateData.bind(this);

    Data.load(this.updateData);
    this._tooltipRefs = [];
    this._debouncedResize = debounce(this._resize, 100).bind(this);
    this.toggleDivision = this.toggleDivision.bind(this);
  }

  componentDidMount() {
    this._animationFrame = window.requestAnimationFrame(this._animate);
    this._stats = new Stats();
    this._stats.showPanel( 0 ); // 0: fps, 1: ms, 2: mb, 3+: custom
    // document.body.appendChild( this._stats.dom );
    window.addEventListener('resize', this._debouncedResize);
    window.addEventListener('orientationchange', this._debouncedResize);
    this._resize();
  }

  componentWillUnmount() {
    document.body.removeChild(this._stats.dom);
    cancelAnimationFrame(this._animationFrame);
    window.removeEventListener('resize', this._debouncedResize);
    window.removeEventListener('orientationchange', this._debouncedResize);
  }
  componentDidUpdate() {
    this._drawSparklines()
  }

  _animate = () => {
    this._stats.begin();
    this._animationFrame = window.requestAnimationFrame(this._animate);
    this._frame++;
    this._canvas.animate(this.state, this._frame, framesPerDatum);
    this._stats.end();
  }

  _resize() {
    this.setState(this._getDimensions());
  }

  _filterData() {
    return this.state.data.filter(d => {
      return this.state.divisions[d.group];
    });
  }

  _getDimensions() {
    let innerWidth = window.innerWidth || document.body.clientWidth;
    let innerHeight = window.innerHeight || document.body.clientHeight;
    const isCompact = (innerWidth < this.metrics.compact);
    const scale = {
      x: 1,
      y: 0.78,
    };

    let dimensions = null;

    const width = innerWidth * scale.x;
    let height = innerHeight * scale.y;
    if (innerWidth > this.metrics.tiny && height < this.metrics.minHeight) {
      height = this.metrics.minHeight * scale.y;
    }
    dimensions = { width, height };

    dimensions.innerWidth = innerWidth;
    dimensions.innerHeight = innerHeight;

    const sidebarWidth = 230;
    dimensions.width -= sidebarWidth
    dimensions.sidebarWidth = sidebarWidth;

    if (innerWidth <= this.metrics.tiny) {
      this.metrics.fontSize = 16 * 0.35;
      dimensions.width += dimensions.sidebarWidth;
      dimensions.sidebarWidth = 0;
    } else if (isCompact) {
      this.metrics.fontSize = 16 * 0.6;
    } else {
      this.metrics.fontSize = 16 * 0.75;
    }
    this.scales.x.range([this.metrics.axisPadding, dimensions.width - (this.metrics.padding)]);
    this.scales.y.range([this.metrics.padding, dimensions.height - this.metrics.padding]);

    if (typeof this.state !== 'undefined') {
      this._rescaleData(this.state.data);
      dimensions.lines = this.getCurves(this.state.data);
    }
    dimensions.selectedDate = null;

    return dimensions;
  }

  getCurves(data) {

    return nest().key(d => d.group).entries(data).map(d => {
      return {
        group: d.key,
        data: polynomial(
          d.values.map(v => {
            return [v.date, v.value];
          }),
          {
            order: 3,
            precision: 40,
          },
        ).points.map(p => {
          // rounding to 2 decimal places avoid path drawing issues
          // see: https://oreillymedia.github.io/Using_SVG/extras/ch08-precision.html
          return [
            Math.floor(this.scales.x(new Date(p[0])) * 100) / 100,
            Math.floor(this.scales.y(p[1]) * 100) / 100,
          ];
        }),
      };
    });
  }

  updateData(data) {
    this._dates = new Set(data.map(d => d.date.valueOf()));
    this._dataByDate = {}
    this.dataByGroup = {}
    data.forEach(datum => {
      const dateKey = datum.date.valueOf();
      if (!(dateKey in this._dataByDate)) {
        this._dataByDate[dateKey] = [];
      }
      this._dataByDate[dateKey].push(datum)
      if (!(datum.group in this.dataByGroup)) {
        this.dataByGroup[datum.group] = [];
      }
      this.dataByGroup[datum.group].push(datum);
    })

    this._dataByGroupYearly = {};
    Object.keys(this.dataByGroup).forEach(groupId => {
      this.dataByGroup[groupId].sort((a, b) => a.date.valueOf() - b.date.valueOf())
      const groupData = this.dataByGroup[groupId]
      const byYear = nest()
        .key(d => {
          return d.date.getFullYear()
        })
        .entries(groupData)
      this._dataByGroupYearly[groupId] = [];
      byYear.forEach(yearlyData => {
        const yearlyMean = mean(yearlyData.values, d => d.value)
        this._dataByGroupYearly[groupId].push(yearlyMean)
      })

    })
    this.scales.x
      .domain([new Date(Math.min(...this._dates)), new Date(Math.max(...this._dates))])
      .clamp(true)
    const values = data.map(d => d.value);
    this.scales.y
      .domain([Math.max(...values), Math.min(...values)])
    const groups = [...new Set(data.map(d => d.group))].sort();
    const divisions = this.state.divisions;
    groups.forEach(g => {
      divisions[g] = true;
    });
    this.scales.c
      .domain(groups)
      .range(['#7351a1', '#0066b3', '#40403e', '#50b848', '#00aeef', '#d73689', '#f26522']);
    const lines = this.getCurves(data);

    this._rescaleData(data);
    this._maxFrames = data.length + framesPerDatum
    this._frame = 0
    this.setState({data, lines, divisions});
  }

  _rescaleData(data) {
    data.forEach(datum => {
      const extreme = Math.abs(datum.value) > this.metrics.deviation;
      const circleOpacity = extreme ? 0.8 : 0.2;
      const lineOpacity = extreme ? 1 : 0;

      const x = this.scales.x(datum.date);
      const y1 = this.scales.y(0);
      const y2 = this.scales.y(datum.value);
      const fill = this.scales.c(datum.group);
      datum.extreme = extreme;
      datum.circleOpacity = circleOpacity
      datum.lineOpacity = lineOpacity
      datum.x = x
      datum.y1 = y1
      datum.y2 = y2
      datum.fill = parseInt(fill.substr(1), 16);
    })
  }

  _canvasHover = (xy) => {
    if (!this._dates) return;
    const timestamp = this.scales.x.invert(xy[0]).valueOf()
    let minDateDelta = Number.MAX_VALUE;
    let selectedDate = null;
    this._dates.forEach(date => {
      const dateDelta = Math.abs(date - timestamp);
      if (dateDelta < minDateDelta) {
        minDateDelta = dateDelta;
        selectedDate = date
      }
    })
    this.setState({ selectedDate, tooltipXY: xy, })
  }

  _canvasOut = () => {
    this.setState({ selectedDate: null });
  }

  toggleDivision(division) {
    const divisions = this.state.divisions;
    divisions[division] = !divisions[division];
    this.setState(divisions);
  }

  _getScaledColor(value) {
    if (value < 0) {
      return dryScale(value)
    }
    return wetScale(value)
  }

  _getTooltip() {
    if (this.state.selectedDate == null) {
      return null
    }
    this._tooltipRefs.length = 0;
    const d = new Date(this.state.selectedDate)
    const data = this._dataByDate[this.state.selectedDate].sort((a, b) => a.group - b.group);
    const rows = data.map((datum, index) => {
      const c = this.scales.c(datum.group)
      const name = divisionData[datum.group - 1].name
      const bubbleStyle = {
        backgroundColor: this._getScaledColor(datum.value)
      }
      const display = this.state.divisions[datum.group] ? null : 'none'
      const sign = datum.value > 0 ? '+' : '';
      return (
        <div key={index} className='cf' style={{ fontWeight: datum.extreme ? 'bold' : null, display}}>
          <div className='dot' style={{
            background: c,
          }}>
            &nbsp;
          </div>
          <span className='name'>
            {name}
          </span>
          <canvas ref={c => {this._tooltipRefs.push(c)}} width={sparklineWidth} height={sparklineHeight} />
          <span className='bubble' style={bubbleStyle}>{sign}{datum.value}</span>
        </div>
      )
    })
    const maxHeight = 180;
    const isTiny = this.state.innerWidth <= this.metrics.tiny
    const midPoint = this.state.innerWidth / 2 - this.state.sidebarWidth
    const flipX = (isTiny && this.state.tooltipXY[0] > midPoint)
      || (!isTiny && this.state.tooltipXY[0] > this.state.innerWidth / 2)
    const flip = {
      x: flipX ? '-100%' : '0',
      y: (this.state.tooltipXY[1] + maxHeight) > this.state.height ? '-100%' : '0',
    };
    const xoffset = flipX ? -15 : 15;
    const style = {transform: `translate(${this.state.tooltipXY[0] + xoffset}px, ${this.state.tooltipXY[1]}px) translate(${flip.x}, ${flip.y})` }
    return (
      <div className='tooltip' style={style}>
        <span style={{ fontWeight: 'bold'}}>
          {months[d.getMonth()]} {d.getFullYear()}
        </span>
        <div className='tip-details'>
          {rows}
        </div>
      </div>
    )
  }

  _drawSparklines() {
    if (this.state.selectedDate == null) {
      return
    }
    const canvases = this._tooltipRefs.filter(r => r != null)
    if (canvases.length === 0) {
      return;
    }
    const xScale = scaleLinear()
      .domain([0, this._dates.size])
      .range([0, sparklineWidth])
    const yScale = scaleLinear()
      .domain([-8, 8])
      .range([sparklineHeight, 0])
    const dotXScale = scaleTime()
      .domain(this.scales.x.domain())
      .range([0, sparklineWidth]);

    const groups = [1, 2, 3, 4, 5, 6, 7];
    groups.forEach((group, index) => {
      const groupData = this._dataByGroupYearly[group];
      xScale.domain([0, groupData.length])
      const canvas = canvases[index];
      const context = canvas.getContext('2d')
      context.clearRect(0, 0, sparklineWidth, sparklineHeight)
      context.strokeStyle = '#000000';
      context.beginPath()
      groupData.forEach((datum, datumIndex) => {
        const x = xScale(datumIndex);
        const y = yScale(datum);
        context[datumIndex === 0 ? 'moveTo' : 'lineTo'](x, y)
      })
      context.stroke();
      context.closePath()

      const arcX = dotXScale(this.state.selectedDate)
      const value = this._dataByDate[this.state.selectedDate].find(d => d.group === group).value
      const arcY = yScale(value);

      context.fillStyle = this.scales.c(group)
      context.beginPath()
      context.arc(arcX, arcY, 3, 0, Math.PI * 2, false);
      context.fill()
      context.closePath();
    })
  }

  render() {
    const ymarkers = [8,4,0,-4,-8].map((d) => {
      return (
        <g key={`m-${d}`}>
          <line
            x1={0}
            y1={this.scales.y(d)}
            x2={this.metrics.padding * 0.25}
            y2={this.scales.y(d)}
            stroke='white'
          />
          <line
            x1={this.state.width - (this.metrics.padding * 0.25)}
            y1={this.scales.y(d)}
            x2={this.state.width}
            y2={this.scales.y(d)}
            stroke='white'
          />
          <text
            fontSize={this.metrics.fontSize}
            dy={this.metrics.fontSize / 2}
            transform={`translate(${this.metrics.padding / 4 * 3}, ${this.scales.y(d)})`}
          >
            {d}
          </text>
        </g>
      );
    });

    const ylabels = [
      {
        name: 'Extremely Moist',
        value: 6
      },
      {
        name: 'Moist',
        value: 2
      },
      {
        name: 'Dry',
        value: -2
      },
      {
        name: 'Extremely Dry',
        value: -6
      },
    ].map((d) => {
      return (
        <text
          key={`l-${d.name}`}
          fontSize={this.metrics.fontSize}
          transform={`translate(${this.metrics.padding}, ${this.scales.y(d.value)}) rotate(-90)`}
          textAnchor='middle'
        >
          {d.name}
        </text>
      );
    });

    const yaxis = this.state.data.length ? (
      <g>
        <g id='gradients'>
          <rect
            fill={wetColor}
            stroke='none'
            x={0}
            y={-450}
            width={this.metrics.padding / 4}
            height={480}
          />
          <rect
            fill='url(#moist)'
            stroke='none'
            x={0}
            y={this.scales.y(this.scales.y.domain()[0])}
            width={this.metrics.padding / 4}
            height={this.scales.y(0) - this.metrics.padding}
          />
          <rect
            fill={wetColor}
            stroke='none'
            x={this.state.width - this.metrics.padding / 4}
            y={-450}
            width={this.metrics.padding / 4}
            height={480}
          />
          <rect
            fill='url(#moist)'
            stroke='none'
            x={this.state.width - this.metrics.padding / 4}
            y={this.scales.y(this.scales.y.domain()[0])}
            width={this.metrics.padding / 4}
            height={this.scales.y(0) - this.metrics.padding}
          />
          <rect
            fill={dryColor}
            stroke='none'
            x={0}
            y={this.scales.y(this.scales.y.domain()[1]) - this.metrics.padding / 4}
            width={this.metrics.padding / 4}
            height={640}
          />
          <rect
            fill='url(#dry)'
            stroke='none'
            x={0}
            y={this.scales.y(0)}
            width={this.metrics.padding / 4}
            height={this.scales.y(this.scales.y.domain()[1]) - this.scales.y(0)}
          />
          <rect
            fill={dryColor}
            stroke='none'
            x={this.state.width - this.metrics.padding / 4}
            y={this.scales.y(this.scales.y.domain()[1]) - this.metrics.padding / 4}
            width={this.metrics.padding / 4}
            height={640}
          />
          <rect
            fill='url(#dry)'
            stroke='none'
            x={this.state.width - this.metrics.padding / 4}
            y={this.scales.y(0)}
            width={this.metrics.padding / 4}
            height={this.scales.y(this.scales.y.domain()[1]) - this.scales.y(0)}
          />
        </g>
        <g id='ymarkers'>
          {ymarkers}
        </g>
        <g id='ylabels'>
          {ylabels}
        </g>
        <g id='lines'>
          <line
            x1={this.metrics.axisPadding / 4 * 3}
            y1={this.scales.y(this.metrics.deviation)}
            x2={this.state.width - (this.metrics.padding / 4 * 3)}
            y2={this.scales.y(this.metrics.deviation)}
            stroke='black'
            strokeWidth={0.5}
            strokeDasharray='2 2'
            fill='none'
          />
          <line
            x1={this.metrics.axisPadding / 4 * 3}
            y1={this.scales.y(0)}
            x2={this.state.width - (this.metrics.padding / 4 * 3)}
            y2={this.scales.y(0)}
            stroke='black'
            strokeWidth={0.5}
            fill='none'
          />
          <line
            x1={this.metrics.axisPadding / 4 * 3}
            y1={this.scales.y(this.metrics.deviation * -1)}
            x2={this.state.width - (this.metrics.padding / 4 * 3)}
            y2={this.scales.y(this.metrics.deviation * -1)}
            stroke='black'
            strokeWidth={0.5}
            strokeDasharray='2 2'
            fill='none'
          />
        </g>
      </g>
    ) : '';

    const years = [];
    const minYear = this.scales.x.domain()[0].getFullYear()
    let year = this.scales.x.domain()[1].getFullYear();
    while (year >= minYear) {
      years.push(year);
      year -= 10;
    }
    const xaxis = years.map((d) => {
      return (
        <text
          transform={`translate(${this.scales.x(new Date(d, 0, 1))}, ${this.state.height - (this.metrics.fontSize / 2)})`}
          fontSize={this.metrics.fontSize}
          textAnchor='middle'
          key={`y-${d}`}
        >
          {d}
        </text>
      );
    });

    const grid = years.map((y) => {
      return [8, 6, 4, 2, 0, -2, -4, -6, -8].map((v) => {
        return (
          <g
            key={`g-${y}-${v}`}
            transform={`translate(${this.scales.x(new Date(y, 0, 1))}, ${this.scales.y(v)})`}
          >
            <line
              x1={-(this.metrics.padding / 8)}
              y1={0}
              x2={(this.metrics.padding / 8)}
              y2={0}
              stroke='grey'
            />
            <line
              x1={0}
              y1={-(this.metrics.padding / 8)}
              x2={0}
              y2={(this.metrics.padding / 8)}
              stroke='grey'
            />
          </g>
        );
      });
    });

    const l = line().x(d => d[0]).y(d => d[1]);
    const lines = this.state.lines.filter(d => {
      return this.state.divisions[d.group];
    }).map(d => {
      return (
        <path key={`l-${d.group}`} d={`${l(d.data)}`} fill='none' stroke={this.scales.c(d.group)} strokeWidth='2' />
      );
    });

    const tweetUrl = encodeURI('https://twitter.com/intent/tweet?text=Drought Monitor helps you explore trends in air moisture in California since 1895.&url=http://droughtmonitor.pitchinteractive.com');

    return (
      <div className="App">
        <header style={{
          width: this.state.width,
          transform: `translateX(-${this.state.sidebarWidth / 2}px)`,
        }}>
          <div className='titleHeader'>
            <h1 className='App-title'>
              Drought Monitor
            </h1>
            <div className='App-info'>
              <p>
                Drought Monitor helps you explore trends in air moisture in California since 1895. Select a point to view values for that month and year. Use the region list to hide or show values for each area. The chart below is based on the
                <a
                  target='_blank'
                  rel="noopener noreferrer"
                  href='https://www.ncdc.noaa.gov/temp-and-precip/drought/historical-palmers/overview'
                >
                  <span className='italic'>
                    {` Palmer Hydrological Drought Index`}
                  </span>
                </a>
                , a soil-moisture algorithm designed to measure the long-term impact of drought by taking into account reservoir levels, groundwater data and other slow-moving indicators. 
              </p>
            </div>
            <div className='App-info'>
              <p>
                The data make clear that although extreme conditions—both wet and dry—have become more frequent since the 1970s, all regions of California have experienced an overall drying trend in recent decades. This visualization is based on a piece about California's drought created for
                <a
                  target='_blank'
                  rel="noopener noreferrer"
                  href='https://www.scientificamerican.com/article/the-science-of-california-s-unprecedented-drought/'
                >
                  <span className='italic'>
                    {` Scientific American `}
                  </span> 
                </a>
                in 2015.
              </p>
            </div>
          </div>
        </header>
        {/* */}
        <div className='sidebar' style={{
          left: (this.state.innerWidth + this.state.width - this.state.sidebarWidth)/ 2,
          width: this.state.sidebarWidth === 0 ? 'auto' : this.state.sidebarWidth
        }}>
          <div className='title'>
            <Divisions
              width={this.metrics.divisionSize}
              height={this.metrics.divisionSize}
              color={this.scales.c}
              data={this.state.divisions}
              click={null}
              title
            />
            <div className='california'>California</div>
            <div className='drought'>Drought</div>
          </div>
          <div className='Region-info'>
            <p>
              Each dot on the graph represents one month’s Palmer Hydrological Drought Index value for each regional division. Dots within the “normal” range (+4 to -4) are faded into the background.
            </p>
          </div>
          <div
            className='key divisions'
          >
            <Divisions
              width={this.metrics.divisionSize}
              height={this.metrics.divisionSize}
              color={this.scales.c}
              data={this.state.divisions}
              click={this.toggleDivision}
            />
          </div>
          <div
            className='sidebarFooter links'
          >
            <div className='social'>
              <div className='icon'>
                <a
                  target='_blank'
                  rel="noopener noreferrer"
                  href={tweetUrl}
                >
                  <img src={twitterLogo} alt='twitter' />
                </a>
              </div>
              <div className='icon'>
                <a
                  target='_blank'
                  rel="noopener noreferrer"
                  href='https://www.facebook.com/sharer.php?u=http://droughtmonitor.pitchinteractive.com/'
                >
                  <img src={facebookLogo} alt='facebook' />
                </a>
              </div>
            </div>
            <div className='bottom'>
              <span className='italic'>Created by:</span> <br />
              <a
                className='pitch'
                target='_blank'
                rel="noopener noreferrer"
                href='http://pitchinteractive.com/'
              >
                Pitch Interactive
              </a>
            </div>
          </div>
        </div>
        {/* */}
        <div className="viz" style={{
          width: this.state.width,
          height: this.state.height,
          transform: `translateX(-${this.state.sidebarWidth / 2}px)`,
        }}>
          <svg
            width={this.state.width}
            height={this.state.height}
            style={{
              background: 'white',
            }}
          >
            <defs>
              <linearGradient id='moist' x2='0%' y2='100%'>
                <stop offset='0%' stopColor={wetColor} />
                <stop offset='100%' stopColor='white' />
              </linearGradient>
              <linearGradient id='dry' x2='0%' y2='100%'>
                <stop offset='0%' stopColor='white' />
                <stop offset='100%' stopColor={dryColor} />
              </linearGradient>
            </defs>
            <g id='grid'>{grid}</g>
            <g id='yaxis'>{yaxis}</g>
            <g id='xaxis'>{xaxis}</g>
          </svg>
          <PixiCanvas
            ref={c => (this._canvas = c)}
            width={this.state.width}
            height={this.state.height}
            data={this.state.data}
            date={this.state.selectedDate}
            onHover={this._canvasHover}
            onOut={this._canvasOut}
          />
          <svg
            width={this.state.width}
            height={this.state.height}
            className='lines'
          >
            <g id='lines' style={{
              transition: (this.state.selectedDate == null) ? 'stroke-opacity 0.5s ease' : '',
              strokeOpacity: (this.state.selectedDate == null) ? 1 : 0.2,
            }}>{lines}</g>
          </svg>
          {this._getTooltip()}
          <div
            className='scrollTarget'
            style={{
              width: (this.metrics.padding * 2) - 2,
              height: this.state.height,
            }}
          ></div>
          <div
            className='scrollTarget'
            style={{
              marginLeft: (this.state.width - this.metrics.padding) + 2,
              width: (this.metrics.padding * 1) - 2,
              height: this.state.height,
            }}
          ></div>
        </div>

        <div className='footer' style={{
          width: this.state.width - 12,
          transform: `translateX(-${this.state.sidebarWidth / 2}px)`,
        }}>
          <span className='italic'>
            Data Source:
          </span>
          &nbsp;
          <a
            className='source'
            target='_blank'
            rel="noopener noreferrer"
            href='https://www7.ncdc.noaa.gov/CDO/CDODivisionalSelect.jsp#'
          >
            National Oceanic and Atmospheric Administration
          </a>
        </div>

      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js